import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { FirstStep } from '../../../app/components/Onboarding/Steps/FirstStep';
import { Loader } from '../../Loader';

export enum Steps {
  First = 0,
  Second = 1,
  Third = 2,
  Fourth = 3,
  Fifth = 4,
  Sixth = 5,
  Seventh = 6,
}

export enum OnboardingStep {
  InitialSetup = 'initial_setup',
  FeatureSelection = 'feature_selection',
  ApprovalWorkflows = 'approval_workflows',
  ImportUsers = 'import_users',
  SendInvitations = 'send_invitations',
  SendInvitationsMessageTemplate = 'send_invitations_message_template',
  SendInvitationsSummary = 'send_invitations_summary',
  Complete = 'complete',
  AbsenceManagementConfiguration = 'absence_management_configuration',
}

export function OnboardingLayout() {
  const [currentStep, setCurrentStep] = useState(Steps.First);

  const navigate = useNavigate();
  const location = useLocation();

  const { data: onboardingStatus, isFetched: isStatusFetched } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_HOST}/v1/onboarding`,
      );
      return data;
    },
    staleTime: 60_000,
  });

  const stepFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const stepKey = params.get('step');

    if (stepKey === OnboardingStep.InitialSetup) {
      return Steps.First;
    }

    return null;
  }, [location.search]);

  const status = onboardingStatus?.data?.status;
  // An explicit ?step= link re-runs that step even after onboarding. Without
  // one, wait for the status (a cold load has none on the first render) and
  // send finished workspaces home.
  const awaitingStatus = stepFromQuery === null && !isStatusFetched;
  const shouldLeave =
    stepFromQuery === null &&
    isStatusFetched &&
    Boolean(status) &&
    status !== 'onboarding';

  useEffect(() => {
    if (stepFromQuery !== null) {
      setCurrentStep(stepFromQuery);
    }
  }, [stepFromQuery]);

  useEffect(() => {
    if (shouldLeave) {
      navigate('/', { replace: true });
    }
  }, [navigate, shouldLeave]);

  const handleNext = () => {
    setCurrentStep(Steps.First);
  };

  const handlePrevious = () => {
    setCurrentStep(Steps.First);
  };

  const handleSkipForNow = () => {
    navigate('/');
  };

  if (awaitingStatus || shouldLeave) {
    return <Loader />;
  }

  return (
    <div className="flex w-screen from-[rgb(254,233,233,0.5)] via-[rgb(255,190,157,0.6)] to-[rgb(254,233,233,0.5)] md:bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))]">
      <FirstStep
        currentStep={currentStep}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        handleSkipForNow={handleSkipForNow}
      />
    </div>
  );
}
